import { Component, Input, OnInit, } from '@angular/core';
import { Column } from '@kaiser-software/chassis-datatable/src/classes/column';
import { Boolean, Currency, Data, Date, DateTime, Id, Images, Label, MultiSelect, Numeric, Progress, Select, StateChangeEvent, Text } from '@kaiser-software/chassis-datatable';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { QueryBuilder } from '@kaiser-software/chassis-resource/src/utils/query-builder';
import { ResourceService } from '@kaiser-software/chassis-resource';
import { ChassisEntityCollectionServiceBase } from '@kaiser-software/chassis-resource/src/services/chassis-entity-collection-service-base';
import { IAbstractEntity } from '@kaiser-software/chassis-resource/src/interfaces';
import { snakeCaseIt } from "case-it"

@Component({
    selector: 'chassis-nova-datatable',
    templateUrl: './datatable.component.html',
    styleUrls: ['./datatable.component.scss'],
})

export class DatatableComponent implements OnInit {

    /**
     * The number of records to take.`
     */
    @Input() public take = 10;

    /**
     * The page number for the pagination.
     * Use of `page` will override `skip`.
     */
    @Input() public page = 1;

    /**
     * The list of columns.
     */
    @Input() public columns: Array<Column> = [];

    /**
     * The hidden column names.
     */
    @Input() public hidden: Array<string> = [];

    @Input() public data: Data = {
        data: [],
        total: 0,
    };

    @Input() public resource: string;

    protected service: ChassisEntityCollectionServiceBase<IAbstractEntity>;

    public constructor(
        protected resourceService: ResourceService
    ) {
        //
    }

    public ngOnInit(): void {
        if (this.resource) {
            this.service = this.resourceService.get(this.resource);

            this.service
                .get()
                .subscribe((entities: IAbstractEntity[]) => {
                    if (!this.data.data.length && entities.length) {
                        this.columns = this.mapColumns(entities);
                    }

                    const results = this.mapResults(entities);

                    this.data = {
                        data: results.slice((this.page - 1) * this.take, this.page * this.take + this.take),
                        total: results.length,
                    };

                    console.log(this.data);
                });
        }
    }

    /**
     * When the stage changes
     *
     * @param event
     */
    public onChange(event: StateChangeEvent): void {
        const state = event.state;

        if (this.service && event.type !== 'column') {
            if (state.filter && state.filter.filters && state.filter.filters.length > 0) {
                state.filter.filters.forEach((filter: CompositeFilterDescriptor) => {
                    if (!filter.hasOwnProperty('logic') || filter.logic === 'and') {
                        filter.filters.forEach((subFilter: FilterDescriptor) => {
                            const mappedFilter: FilterDescriptor = this.mapFilter(subFilter);

                            this.service = this.service.where(
                                <string>mappedFilter.field,
                                <string>mappedFilter.operator,
                                mappedFilter.value
                            );
                        });
                    } else if (filter.logic === 'or') {
                        this.service = this.service.where((q: QueryBuilder) => {
                            filter.filters.forEach((subFilter: FilterDescriptor, index: number) => {
                                const mappedFilter: FilterDescriptor = this.mapFilter(subFilter);

                                if (index === 0) { // first filter
                                    q.where(
                                        <string>mappedFilter.field,
                                        <string>mappedFilter.operator,
                                        mappedFilter.value
                                    );
                                } else {
                                    q.orWhere(
                                        <string>mappedFilter.field,
                                        <string>mappedFilter.operator,
                                        mappedFilter.value
                                    );
                                }
                            });
                        });
                    }
                });
            }

            switch (event.type) {
                case 'sort':
                    const skip = event.state.skip;
                    const take = event.state.take;

                    console.log({skip, take});

                    break;
            }

            this.service.get().subscribe((response) => {
                const results = this.mapResults(response);

                this.data = {
                    data: results.slice(state.skip, state.skip + state.take),
                    total: results.length,
                };
            });
        }
    }

    /**
     * Map the filter to a instruction that can be executed on the query builder
     *
     * @param filter
     */
    protected mapFilter(filter: FilterDescriptor): FilterDescriptor {
        const mappedFilter: FilterDescriptor = Object.create(filter);

        switch (mappedFilter.operator) {
            case 'eq':
                mappedFilter.operator = '=';
                break;
            case 'neq':
                mappedFilter.operator = '!=';
                break;
            case 'isnull':
                mappedFilter.operator = '=';
                mappedFilter.value    = null;
                break;
            case 'isnotnull':
                mappedFilter.operator = '!=';
                mappedFilter.value    = null;
                break;
            case 'lt':
                mappedFilter.operator = '<';
                break;
            case 'lte':
                mappedFilter.operator = '<=';
                break;
            case 'gt':
                mappedFilter.operator = '>';
                break;
            case 'gte':
                mappedFilter.operator = '>=';
                break;
            case 'contains':
                mappedFilter.operator = 'LIKE';
                mappedFilter.value    = '%' + mappedFilter.value + '%';
                break;
            case 'doesnotcontain':
                mappedFilter.operator = 'NOT LIKE';
                mappedFilter.value    = '%' + mappedFilter.value + '%';
                break;
            case 'startswith':
                mappedFilter.operator = 'LIKE';
                mappedFilter.value    = mappedFilter.value + '%';
                break;
            case 'endswith':
                mappedFilter.operator = 'LIKE';
                mappedFilter.value    = '%' + mappedFilter.value;
                break;
            case 'isempty':
                mappedFilter.operator = '=';
                mappedFilter.value    = null;
                break;
            case 'isnotempty':
                mappedFilter.operator = '!=';
                mappedFilter.value    = null;
                break;
        }

        return mappedFilter;
    }

    /**
     * Map the columns based on a response
     *
     * @param response
     */
    protected mapColumns(response: Array<any>): Array<any> {
        if (response.length === 0) {
            return [];
        }

        const columns: Array<any> = [];

        response[0]._fields.forEach((field: any) => {
            if (field.attribute === 'ComputedField') {
                field.attribute = snakeCaseIt(field.name);
            }

            switch (field.component) {
                case 'advanced-media-library-field':
                    columns.push(new Images({
                        attribute: field.attribute,
                        name: field.name
                    }));

                    break;
                case 'boolean-field':
                    columns.push(new Boolean({
                        attribute: field.attribute,
                        name: field.name
                    }));

                    break;
                case 'currency-field':
                    columns.push(new Currency({
                        attribute: field.attribute,
                        name: field.name
                    }));

                    break;
                case 'date':
                    columns.push(new Date({
                        attribute: field.attribute,
                        name: field.name
                    }));

                    break;
                case 'date-time':
                    columns.push(new DateTime({
                        attribute: field.attribute,
                        name: field.name
                    }));

                    break;
                case 'id-field':
                    columns.push(new Id({
                        attribute: field.attribute,
                        name: field.name
                    }));

                    break;
                case 'label-field':
                    columns.push(new Label({
                        attribute: field.attribute,
                        name: field.name,
                        data: field.options
                    }));

                    break;
                case 'mulit-select-field':
                    columns.push(new MultiSelect({
                        attribute: field.attribute,
                        name: field.name,
                        data: field.options
                    }));

                    break;
                case 'number-field':
                    columns.push(new Numeric({
                        attribute: field.attribute,
                        name: field.name
                    }));

                    break;
                case 'progress-field':
                    columns.push(new Progress({
                        attribute: field.attribute,
                        name: field.name
                    }));

                    break;
                case 'select-field':
                    columns.push(new Select({
                        attribute: field.attribute,
                        name: field.name,
                        data: field.options
                    }));

                    break;
                case 'text-field':
                    columns.push(new Text({
                        attribute: field.attribute,
                        name: field.name
                    }));

                    break;
                default:
                    columns.push(new Text({
                        attribute: field.attribute,
                        name: field.name
                    }));

                    break;
            }
        });

        return columns;
    }

    /**
     * Map the filter to a instruction that can be executed on the query builder
     *
     * @param response
     */
    protected mapResults(response: Array<any>): Array<any> {
        return response.map((row: any) => {
            const fields = {};

            row._fields.forEach((field: any) => {
                if (field.attribute === 'ComputedField') {
                    field.attribute = snakeCaseIt(field.name);
                }

                fields[field.attribute] = this.mapField(field);
            });

            return fields;
        });
    }

    /**
     *
     * @param field
     */
    protected mapField(field: any): any {
        switch (field.component) {
            case 'progress-field':
                return {
                    value: field.value,
                    classes: 'todo', // TODO
                };
            case 'label-field':
                return {
                    value: field.value,
                    classes: 'todo', // TODO
                };
            case 'advanced-media-library-field':
                return field;
            default:
                return field.value;
        }
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-nova/src/components/datatable/datatable.component.ts